"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloudOutlined,
  CodeOutlined,
  CopyOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from "antd";
import type { Configuration } from "../domain/models";
import { PageHeader, serviceName } from "../components/common";
import { roles, services } from "../mocks/data";
import {
  deliveryApi,
  diffConfiguration,
  type ConfigurationDiff,
  type DeliveryActor,
} from "../services/deliveryApi";
import { useOpsStore } from "../stores/useOpsStore";

interface ConfigurationFormValues {
  serviceId: string;
  namespace: string;
  content: Record<string, string>;
  changeNote: string;
  approvalNo: string;
  confirmProduction: boolean;
}

const makeIdempotencyKey = () =>
  globalThis.crypto?.randomUUID?.() ?? `config-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const diffType = {
  added: { label: "新增", color: "green" },
  changed: { label: "修改", color: "blue" },
  removed: { label: "删除", color: "red" },
} as const;

const displayValue = (value: string | undefined, sensitive = false) =>
  sensitive && value !== undefined ? "******" : value ?? "-";

export function ConfigurationPage() {
  const { message } = App.useApp();
  const currentUser = useOpsStore((state) => state.currentUser);
  const [form] = Form.useForm<ConfigurationFormValues>();
  const [items, setItems] = useState<Configuration[]>([]);
  const [selected, setSelected] = useState<Configuration>();
  const [history, setHistory] = useState<Configuration[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [publishBase, setPublishBase] = useState<Configuration>();
  const [reviewDiff, setReviewDiff] = useState<ConfigurationDiff[]>([]);
  const [reviewValues, setReviewValues] = useState<ConfigurationFormValues>();
  const [publishing, setPublishing] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const role = roles.find((item) => item.id === currentUser?.roleId);
  const canPublish = currentUser?.roleId === "role-admin" || currentUser?.roleId === "role-ops-admin";
  const actor: DeliveryActor | undefined = currentUser && role ? {
    name: currentUser.displayName,
    roleId: currentUser.roleId as DeliveryActor["roleId"],
    roleName: role.name,
  } : undefined;

  const loadConfigurations = async (preferredServiceId?: string) => {
    const nextItems = await deliveryApi.listConfigurations();
    const nextSelected = nextItems.find((item) => item.serviceId === preferredServiceId)
      ?? nextItems.find((item) => item.serviceId === selected?.serviceId)
      ?? nextItems[0];
    setItems(nextItems);
    setSelected(nextSelected);
    if (nextSelected) {
      setHistory(await deliveryApi.listConfigurationHistory(nextSelected.serviceId, nextSelected.environment));
    }
  };

  useEffect(() => {
    let cancelled = false;
    void deliveryApi.listConfigurations().then(async (nextItems) => {
      const nextSelected = nextItems[0];
      const nextHistory = nextSelected
        ? await deliveryApi.listConfigurationHistory(nextSelected.serviceId, nextSelected.environment)
        : [];
      if (!cancelled) {
        setItems(nextItems);
        setSelected(nextSelected);
        setHistory(nextHistory);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const selectConfiguration = async (configuration: Configuration) => {
    setSelected(configuration);
    setHistory(await deliveryApi.listConfigurationHistory(configuration.serviceId, configuration.environment));
  };

  const openPublish = (configuration = selected) => {
    if (!configuration || !canPublish) return;
    setPublishBase(configuration);
    setWorkflowStep(0);
    setReviewDiff([]);
    setReviewValues(undefined);
    setIdempotencyKey(makeIdempotencyKey());
    form.setFieldsValue({
      serviceId: configuration.serviceId,
      namespace: configuration.namespace,
      content: { ...configuration.content },
      changeNote: "",
      approvalNo: `CHG-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}-`,
      confirmProduction: false,
    });
    setDrawerOpen(true);
  };

  const changePublishService = (serviceId: string) => {
    const configuration = items.find((item) => item.serviceId === serviceId);
    if (!configuration) return;
    setPublishBase(configuration);
    setReviewDiff([]);
    setReviewValues(undefined);
    form.setFieldsValue({
      serviceId,
      namespace: configuration.namespace,
      content: { ...configuration.content },
      confirmProduction: false,
    });
  };

  const prepareReview = async () => {
    const values = await form.validateFields();
    if (!publishBase) return;
    const nextContent = { ...publishBase.content, ...values.content };
    const diff = diffConfiguration(publishBase.content, nextContent, publishBase.sensitiveKeys);
    if (diff.length === 0) {
      message.warning("配置内容没有变化，无需发布");
      return;
    }
    setReviewValues({ ...values, content: nextContent });
    setReviewDiff(diff);
    setWorkflowStep(1);
  };

  const publish = async () => {
    if (!publishBase || !actor || !reviewValues) return;
    setPublishing(true);
    try {
      const result = await deliveryApi.publishConfiguration({
        serviceId: publishBase.serviceId,
        environment: publishBase.environment,
        namespace: reviewValues.namespace,
        baseVersion: publishBase.version,
        content: reviewValues.content,
        sensitiveKeys: publishBase.sensitiveKeys,
        changeNote: reviewValues.changeNote,
        approvalNo: reviewValues.approvalNo,
        confirmProduction: reviewValues.confirmProduction,
        idempotencyKey,
        actor,
      });
      await loadConfigurations(result.configuration.serviceId);
      setDrawerOpen(false);
      message.success(`配置 v${result.configuration.version} 发布成功，审计记录已生成`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "配置发布失败");
    } finally {
      setPublishing(false);
    }
  };

  const copyConfiguration = async () => {
    if (!selected) return;
    const text = Object.entries(selected.content).map(([key, value]) => `${key}=${value}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      message.success("已复制脱敏后的配置内容");
    } catch {
      message.error("浏览器未授予剪贴板权限");
    }
  };

  const reviewColumns = useMemo(() => [
    {
      title: "配置项",
      dataIndex: "key",
      render: (value: string, record: ConfigurationDiff) =>
        <Space><Typography.Text code>{value}</Typography.Text>{record.sensitive && <LockOutlined className="warning-text" />}</Space>,
    },
    {
      title: "变更",
      dataIndex: "type",
      width: 90,
      render: (value: ConfigurationDiff["type"]) => <Tag color={diffType[value].color}>{diffType[value].label}</Tag>,
    },
    {
      title: "原值",
      dataIndex: "before",
      render: (value: string | undefined, record: ConfigurationDiff) =>
        <Typography.Text delete={record.type === "removed"}>{displayValue(value, record.sensitive)}</Typography.Text>,
    },
    {
      title: "新值",
      dataIndex: "after",
      render: (value: string | undefined, record: ConfigurationDiff) =>
        <Typography.Text type={record.type === "removed" ? "secondary" : undefined}>{displayValue(value, record.sensitive)}</Typography.Text>,
    },
  ], []);

  return <>
    <PageHeader
      title="配置中心"
      description="按服务和环境查看配置版本，敏感项默认脱敏"
      extra={
        <Tooltip title={canPublish ? undefined : "当前角色无配置发布权限"}>
          <Button type="primary" icon={<CloudOutlined />} disabled={!canPublish} onClick={() => openPublish()}>
            发布配置
          </Button>
        </Tooltip>
      }
    />
    <Alert
      type="info"
      showIcon
      message="安全提示"
      description="当前执行 Mock 配置发布，仅更新本次浏览器会话，不连接生产配置中心。敏感项保持脱敏，审计记录只保存变更键名。"
      className="page-alert"
    />
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card variant="borderless" title="服务配置">
            <List
              dataSource={items}
              locale={{ emptyText: <Empty description="暂无配置" /> }}
              renderItem={(item) => (
                <List.Item
                  className={`config-list-item ${selected?.id === item.id ? "selected" : ""}`}
                  onClick={() => void selectConfiguration(item)}
                >
                  <List.Item.Meta
                    avatar={<CodeOutlined />}
                    title={serviceName(item.serviceId, services)}
                    description={`生产环境 · v${item.version} · ${item.updatedAt}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col span={16}>
          {selected ? <Card
            variant="borderless"
            title={<Space>{serviceName(selected.serviceId, services)}<Tag color="blue">生产环境</Tag><Tag>v{selected.version}</Tag></Space>}
            extra={<Button icon={<CopyOutlined />} onClick={() => void copyConfiguration()}>复制</Button>}
          >
            <Descriptions column={2} className="config-meta">
              <Descriptions.Item label="命名空间">{selected.namespace}</Descriptions.Item>
              <Descriptions.Item label="最后更新">{selected.updatedAt}</Descriptions.Item>
              <Descriptions.Item label="操作人">{selected.operator}</Descriptions.Item>
              <Descriptions.Item label="变更说明">{selected.changeNote}</Descriptions.Item>
            </Descriptions>
            <div className="code-panel">
              {Object.entries(selected.content).map(([key, value]) => (
                <div className="code-line" key={key}>
                  <span>{key}</span>
                  <b>{selected.sensitiveKeys.includes(key) && <LockOutlined />}{value}</b>
                </div>
              ))}
            </div>
            <Divider />
            <Typography.Title level={5}>版本记录</Typography.Title>
            <Timeline items={history.map((item, index) => ({
              color: index === 0 ? "blue" : "gray",
              children: <><b>v{item.version}{index === 0 ? "（当前）" : ""}</b><p>{item.updatedAt} · {item.operator} · {item.changeNote}</p></>,
            }))} />
          </Card> : <Card><Empty description="请选择服务配置" /></Card>}
        </Col>
      </Row>
    </Spin>

    <Drawer
      className="workflow-drawer"
      title="发布配置"
      width={720}
      open={drawerOpen}
      destroyOnHidden
      maskClosable={!publishing}
      closable={!publishing}
      onClose={() => setDrawerOpen(false)}
      footer={<div className="workflow-footer">
        {workflowStep === 1 && <Button icon={<ArrowLeftOutlined />} disabled={publishing} onClick={() => setWorkflowStep(0)}>返回修改</Button>}
        <div className="filter-spacer" />
        <Button disabled={publishing} onClick={() => setDrawerOpen(false)}>取消</Button>
        {workflowStep === 0
          ? <Button type="primary" onClick={() => void prepareReview()}>下一步：确认变更</Button>
          : <Button type="primary" danger icon={<CloudOutlined />} loading={publishing} onClick={() => void publish()}>确认发布</Button>}
      </div>}
    >
      <Steps
        size="small"
        current={workflowStep}
        items={[{ title: "编辑配置" }, { title: "风险确认" }]}
        className="workflow-steps"
      />
      {workflowStep === 0 ? <Form form={form} layout="vertical" requiredMark="optional">
        <Alert
          type="warning"
          showIcon
          message="生产配置高风险操作"
          description="发布将生成新配置版本并写入审计记录。当前为 Mock 模式，不会调用 Nacos、Apollo 或生产服务。"
          className="page-alert"
        />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="目标服务" name="serviceId" rules={[{ required: true, message: "请选择目标服务" }]}>
              <Select
                showSearch
                optionFilterProp="label"
                onChange={changePublishService}
                options={items.map((item) => ({ value: item.serviceId, label: serviceName(item.serviceId, services) }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="环境">
              <Input value="生产环境" disabled />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="命名空间" name="namespace" rules={[{ required: true, message: "请输入命名空间" }]}>
          <Input />
        </Form.Item>
        <div className="workflow-section-title">
          <span>配置内容</span>
          <Tag>基于 v{publishBase?.version}</Tag>
        </div>
        <div className="config-editor">
          {publishBase && Object.entries(publishBase.content).map(([key, value]) => {
            const sensitive = publishBase.sensitiveKeys.includes(key);
            return <div className="config-edit-row" key={key}>
              <Typography.Text code>{key}</Typography.Text>
              <Typography.Text type="secondary">{displayValue(value, sensitive)}</Typography.Text>
              {sensitive
                ? <Input prefix={<LockOutlined />} value="保留原密文" disabled />
                : <Form.Item name={["content", key]} rules={[{ required: true, message: "配置值不能为空" }]} noStyle><Input /></Form.Item>}
            </div>;
          })}
        </div>
        <Row gutter={16} className="section-row">
          <Col span={12}>
            <Form.Item
              label="变更单号"
              name="approvalNo"
              rules={[
                { required: true, message: "请输入变更单号" },
                { pattern: /^CHG-\d{8}-\d{3,6}$/i, message: "格式：CHG-YYYYMMDD-序号" },
              ]}
            >
              <Input placeholder="CHG-20260725-001" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="发布后版本">
              <Input value={`v${(publishBase?.version ?? 0) + 1}`} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="变更说明" name="changeNote" rules={[{ required: true, min: 4, message: "请填写至少 4 个字的变更说明" }]}>
          <Input.TextArea rows={3} maxLength={200} showCount placeholder="说明变更原因、影响范围和验证方式" />
        </Form.Item>
        <Form.Item
          name="confirmProduction"
          valuePropName="checked"
          rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("请确认生产环境风险")) }]}
        >
          <Checkbox>我已核对目标服务和变更内容，并确认当前仅执行 Mock 发布</Checkbox>
        </Form.Item>
      </Form> : <div>
        <Alert
          type="warning"
          showIcon
          message={`即将发布 ${serviceName(publishBase?.serviceId ?? "", services)} 生产配置`}
          description={`配置版本将从 v${publishBase?.version} 更新为 v${(publishBase?.version ?? 0) + 1}，共 ${reviewDiff.length} 项变更。`}
          className="page-alert"
        />
        <Descriptions bordered size="small" column={2} className="review-descriptions">
          <Descriptions.Item label="变更单号">{reviewValues?.approvalNo}</Descriptions.Item>
          <Descriptions.Item label="操作人">{currentUser?.displayName}</Descriptions.Item>
          <Descriptions.Item label="命名空间">{reviewValues?.namespace}</Descriptions.Item>
          <Descriptions.Item label="执行模式"><Tag color="blue">Mock</Tag></Descriptions.Item>
          <Descriptions.Item label="变更说明" span={2}>{reviewValues?.changeNote}</Descriptions.Item>
        </Descriptions>
        <Typography.Title level={5} className="section-row">配置差异</Typography.Title>
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          columns={reviewColumns}
          dataSource={reviewDiff}
        />
        <div className="production-confirm">
          <CheckCircleOutlined />
          <div><b>二次确认已完成</b><span>重复提交由幂等键保护，敏感配置值不会写入审计日志。</span></div>
        </div>
      </div>}
    </Drawer>
  </>;
}

export default ConfigurationPage;
